import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { makeAsk, ERRORS, Card, Button, Notice, Gate, relative, Avatar, asList, TabBoundary, inputClass, selectClass } from './dashboardShared';

// #seller: your Whatnot sales and buyers, for the bot owner alone. Opened from the username menu.
// The page holds nothing: every number comes from the bot (the whatnotseller cog, through the dashboard cog), which only
// answers the bot owner's Discord login. None of it is ever written to the website's database for visitors to read.

const TABS = [['overview', 'Overview'], ['shows', 'Shows'], ['buyers', 'Buyers'], ['trends', 'Trends']];
const BAR = '#0a8fd0'; // the site's blue, darkened until it passed the chart colour checks against this dark background

const money = (amount, currency) => {
  const value = (amount || 0) / 100;
  try {
    return value.toLocaleString(undefined, { style: 'currency', currency: currency || 'GBP', minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 });
  } catch {
    return value.toFixed(2);
  }
};
const count = (n) => (n == null ? '—' : Number(n).toLocaleString());
const dayLabel = (day) => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const ORDER_STATUS = { pending: 'Awaiting payment', pending_fulfillment: 'To ship', completed: 'Completed', cancelled: 'Cancelled', failed: 'Failed' };
const MEDALS = ['🥇', '🥈', '🥉'];
const LAPSED_DAYS = 60;  // a buyer you "haven't seen in a while" last bought longer ago than this
const ORDER_TYPE = {buy_it_now: 'Buy It Now', auction: 'Auction', giveaway: 'Giveaway', other: 'Other' };

const Tile = ({ label, value, sub }) => (
  <Card className="!p-2.5 sm:!p-4">
    <p className="text-zinc-500 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider truncate">{label}</p>
    <p className="text-white text-base sm:text-xl font-bold truncate">{value}</p>
    <p className="text-zinc-500 text-[10px] sm:text-[11px] truncate">{sub || ' '}</p>
  </Card>
);

const Warning = ({ children }) => (
  <p className="rounded-lg px-3 py-2 text-xs border border-amber-700/50 bg-amber-900/20 text-amber-200">⚠️ {children}</p>
);

// A one-series bar chart with a table view. Each whole column is the hover and tap target, so a tiny bar is still easy
// to reach. items: [{ key, label, value, detail (the line shown for it), cells (its table row) }]
function BarChart({ title, items, columns, summary, ariaLabel, axis, tableNewestFirst }) {
  const [active, setActive] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const top = Math.max(...items.map(d => d.value), 0);
  const shown = active != null ? items[active] : null;
  const rows = tableNewestFirst ? [...items].reverse() : items;
  return (
    <Card title={title} right={<Button small tone="ghost" onClick={() => setAsTable(t => !t)}>{asTable ? 'Show chart' : 'Show table'}</Button>}>
      {asTable ? (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-zinc-500 text-left">{columns.map((c, i) => <th key={c} className={`font-semibold py-1 ${i ? 'text-right' : ''}`}>{c}</th>)}</tr></thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.key} className="border-t border-zinc-800 text-zinc-300">
                  {d.cells.map((cell, i) => <td key={i} className={`py-1 ${i ? 'text-right' : ''}`}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <p className="text-zinc-300 text-xs mb-2 h-4 truncate" aria-live="polite">{shown ? shown.detail : summary}</p>
          <div className="relative">
            <div className="absolute inset-x-0 top-0 border-t border-zinc-800" aria-hidden="true" />
            <div className="flex items-end h-36 border-b border-zinc-700" role="img" aria-label={`${ariaLabel} The table view lists each one.`}
              onMouseLeave={() => setActive(null)}>
              {items.map((d, i) => (
                <button key={d.key} type="button" aria-label={d.detail}
                  onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} onClick={() => setActive(i)}
                  className="flex-1 min-w-0 h-full flex items-end px-px focus:outline-none" style={{ background: active === i ? 'rgba(255,255,255,0.05)' : 'transparent' }}>
                  <span className="block w-full rounded-t" style={{ height: d.value && top ? `${Math.max(3, (d.value / top) * 100)}%` : 0, background: BAR }} />
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-between text-zinc-500 text-[10px] mt-1">{axis.map(a => <span key={a}>{a}</span>)}</div>
        </>
      )}
    </Card>
  );
}

const plural = (n, word) => `${count(n)} ${word}${n !== 1 ? 's' : ''}`;

// Sales for each of the last 30 days
function SalesChart({ days, currency }) {
  const total = days.reduce((sum, d) => sum + d.sales, 0);
  const best = Math.max(...days.map(d => d.sales), 0);
  return (
    <BarChart title="Sales, last 30 days" columns={['Day', 'Sales', 'Orders']} tableNewestFirst
      summary={`${money(total, currency)} in total. Hover or tap a day for its details.`}
      ariaLabel={`Daily sales for the last 30 days, ${money(total, currency)} in total.`}
      axis={[dayLabel(days[0].day), `Best day: ${money(best, currency)}`, 'Today']}
      items={days.map(d => ({
        key: d.day, value: d.sales, detail: `${dayLabel(d.day)}: ${money(d.sales, currency)} from ${plural(d.orders, 'order')}`,
        cells: [dayLabel(d.day), money(d.sales, currency), d.orders],
      }))} />
  );
}

const age = (ms) => (ms ? relative(ms).replace(/ ago$/, '') : '—');

// Orders paid for and waiting to be posted, the ones waiting longest first
function ToShip({ total, list }) {
  return (
    <Card title={`To ship · ${count(total)}`}>
      {list.length === 0 ? <p className="text-zinc-500 text-xs">Nothing waiting. All posted!</p> : (
        <>
          <ul className="divide-y divide-zinc-800 max-h-72 overflow-y-auto">
            {list.map(o => (
              <li key={o.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{o.buyer ? `@${o.buyer}` : 'Buyer not available'}</p>
                  <p className="text-zinc-500 text-[11px] truncate">{ORDER_TYPE[o.type] || 'Order'} · {o.at ? relative(o.at) : ''}</p>
                </div>
                <span className="text-zinc-300 text-[11px] flex-shrink-0">waiting {age(o.at)}</span>
              </li>
            ))}
          </ul>
          {total > list.length && <p className="text-zinc-500 text-[11px] mt-2">Showing the {list.length} waiting longest.</p>}
        </>
      )}
    </Card>
  );
}

// How auctions and Buy It Now compare, over the last 30 days or all time
function SaleTypes({ types, currency }) {
  const [span, setSpan] = useState('month');
  const rows = Object.entries(types?.[span] || {}).sort((a, b) => b[1].sales - a[1].sales);
  const total = rows.reduce((sum, [, t]) => sum + t.sales, 0);
  return (
    <Card title="Auction vs Buy It Now" right={
      <select value={span} onChange={(e) => setSpan(e.target.value)} className={selectClass} aria-label="Period">
        <option value="month">Last 30 days</option>
        <option value="all">All time</option>
      </select>
    }>
      {rows.length === 0 ? <p className="text-zinc-500 text-xs">No sales in this period.</p> : (
        <table className="w-full text-xs">
          <thead><tr className="text-zinc-500 text-left">
            <th className="font-semibold py-1">Type</th><th className="font-semibold py-1 text-right">Sales</th>
            <th className="font-semibold py-1 text-right">Share</th><th className="font-semibold py-1 text-right">Orders</th>
            <th className="font-semibold py-1 text-right">Average</th>
          </tr></thead>
          <tbody>
            {rows.map(([type, t]) => (
              <tr key={type} className="border-t border-zinc-800 text-zinc-300">
                <td className="py-1 text-white font-semibold">{ORDER_TYPE[type] || type}</td>
                <td className="py-1 text-right">{money(t.sales, currency)}</td>
                <td className="py-1 text-right">{total ? Math.round((t.sales / total) * 100) : 0}%</td>
                <td className="py-1 text-right">{count(t.orders)}</td>
                <td className="py-1 text-right">{money(t.orders ? t.sales / t.orders : 0, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function OverviewTab({ ask, failed }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    ask('seller.overview').then((answer) => { if (!failed(answer)) setData(answer.data); });
  }, [ask, failed]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const days = asList(data.days);
  const recent = asList(data.recent);
  const state = data.state || {};
  const problems = asList(state.problems);
  const { today = {}, week = {}, month = {}, all = {} } = data.periods || {};
  const [lastMonth, thisMonth] = asList(data.months);
  return (
    <div className="space-y-4">
      {state.scanning && <Warning>Still reading your order history from Whatnot, so these totals are still going up. Refresh in a little while.</Warning>}
      {!state.scanning && !state.ordersKnown && <p className="text-zinc-500 text-xs">No orders read yet. They'll appear here within a minute or so of the token being set up.</p>}
      {problems.map((p) => <Warning key={p}>{p}</Warning>)}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[['Today', today], ['7 days', week], ['30 days', month], ['All time', all]].map(([label, t]) => (
          <Tile key={label} label={label} value={money(t.sales, data.currency)} sub={`${count(t.orders || 0)} order${t.orders !== 1 ? 's' : ''} · ${count(t.buyers || 0)} buyer${t.buyers !== 1 ? 's' : ''}`} />
        ))}
      </div>
      {thisMonth && lastMonth && (
        <p className="text-zinc-400 text-xs">
          This month so far: <span className="text-white font-semibold">{money(thisMonth.sales, data.currency)}</span>
          {' '}· all of last month: <span className="text-white font-semibold">{money(lastMonth.sales, data.currency)}</span>
        </p>
      )}
      <p className="text-zinc-500 text-[11px]">
        Sales are the price of paid items, before shipping, tax and fees. Giveaways and cancelled orders aren't counted.
      </p>
      <ToShip total={data.toShip || 0} list={asList(data.shipList)} />
      {days.length > 0 && <SalesChart days={days} currency={data.currency} />}
      <SaleTypes types={data.types} currency={data.currency} />
      <Card title="Latest orders">
        {recent.length === 0 ? <p className="text-zinc-500 text-xs">Nothing yet.</p> : (
          <ul className="divide-y divide-zinc-800">
            {recent.map((o) => (
              <li key={o.id} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{o.buyer ? `@${o.buyer}` : 'Buyer not available'}</p>
                  <p className="text-zinc-500 text-[11px] truncate">{ORDER_TYPE[o.type] || 'Order'} · {ORDER_STATUS[o.status] || o.status} · {o.at ? relative(o.at) : ''}</p>
                </div>
                <span className="text-white text-xs font-semibold flex-shrink-0">{money(o.amount, o.currency)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

const SHOW_WAITING = {
  NOT_ENDED: "Whatnot hasn't finished this show.",
  PENDING: "Whatnot doesn't have stats for this show yet.",
  WILL_NOT_BE_CALCULATED: "Whatnot doesn't make stats for private, rehearsal or cancelled shows.",
  UNAVAILABLE: "Couldn't load this show's stats just now.",
};

function ShowsTab({ ask, failed }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    ask('seller.shows').then((answer) => { if (!failed(answer)) setData(answer.data); });
  }, [ask, failed]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const upcoming = asList(data.upcoming);
  const past = asList(data.past);
  return (
    <div className="space-y-4">
      <Card title="Coming up">
        {upcoming.length === 0 ? <p className="text-zinc-500 text-xs">No shows scheduled.</p> : (
          <ul className="divide-y divide-zinc-800">
            {upcoming.map((s) => (
              <li key={s.id} className="py-2 flex items-center gap-2">
                <p className="text-white text-xs font-semibold flex-1 min-w-0 truncate">{(s.title || 'Untitled').replace(/\s*\n\s*/g, ' ')}</p>
                {s.visibility === 'private' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-200">Private</span>}
                <span className="text-zinc-500 text-[11px] flex-shrink-0">{s.at ? new Date(s.at).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'No time set'}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card title="Past shows">
        {past.length === 0 ? <p className="text-zinc-500 text-xs">No past shows yet.</p> : (
          <ul className="divide-y divide-zinc-800">
            {past.map((s) => (
              <li key={s.id} className="py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-white text-xs font-semibold min-w-0 truncate">{(s.title || 'Untitled').replace(/\s*\n\s*/g, ' ')}</p>
                  <span className="text-zinc-500 text-[11px] flex-shrink-0">{s.at ? new Date(s.at).toLocaleDateString() : ''}</span>
                </div>
                {s.status !== 'GENERATED' ? <p className="text-zinc-500 text-[11px] mt-1">{SHOW_WAITING[s.status] || 'No stats for this show.'}</p> : (
                  <dl className="grid grid-cols-3 sm:grid-cols-6 gap-x-3 gap-y-2 mt-2">
                    {[['Sales', money(s.sales, s.currency)], ['Earnings (est.)', money(s.earnings, s.currency)],
                      ['Sales per hour', s.minutes > 0 ? money(s.sales / (s.minutes / 60), s.currency) : '—'], ['Orders', count(s.orders)],
                      ['Buyers', s.buyers == null ? '—' : `${count(s.buyers)}${s.firstTime != null ? ` (${count(s.firstTime)} new)` : ''}`],
                      ['Returning buyers', count(s.returning)],
                      ['Viewers who bought', s.viewers > 0 && s.buyers != null ? `${((s.buyers / s.viewers) * 100).toFixed(1)}%` : '—'],
                      ['Unique viewers', count(s.viewers)], ['Peak viewers', count(s.peak)], ['Total views', count(s.views)],
                      ['Length', s.minutes > 0 ? `${Math.floor(s.minutes / 60)}h ${Math.round(s.minutes % 60)}m` : '—'],
                      ['Giveaways', count(s.giveaways)], ['Rating', s.rating != null ? Number(s.rating).toFixed(1) : '—']].map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <dt className="text-zinc-500 text-[10px] truncate">{label}</dt>
                        <dd className="text-white text-xs font-semibold truncate">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {asList(s.topBuyers).length > 0 && (
                  <p className="text-zinc-400 text-[11px] mt-2 truncate">
                    <span className="text-zinc-500">Top buyers: </span>
                    {asList(s.topBuyers).map((b, i) => `${MEDALS[i]} @${b.username} ${money(b.spent, s.currency || 'GBP')}`).join('   ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// One buyer. Tapping opens every order they've placed, and which show (or the shop) it was from
function BuyerRow({ buyer: b, currency, ask, failed }) {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const toggle = async () => {
    setOpen(o => !o);
    if (history) return;
    const answer = await ask('seller.buyer', { username: b.username });
    setHistory(failed(answer) ? [] : asList(answer.data.orders));
  };
  return (
    <li className="py-2">
      <button type="button" onClick={toggle} aria-expanded={open} className="w-full flex items-center gap-2 text-left">
        <Avatar src={b.member?.avatar} name={b.username} size={28} />
        <div className="min-w-0 flex-1">
          <p className="text-white text-xs font-semibold truncate">@{b.username}</p>
          <p className="text-zinc-500 text-[11px] truncate">
            {plural(b.orders, 'order')} · last {b.last ? relative(b.last) : '—'}{b.member ? ` · ${b.member.name} in the server` : ' · not in the server'}
          </p>
        </div>
        <span className="text-white text-xs font-semibold flex-shrink-0">{money(b.spent, currency)}</span>
        <span className="text-zinc-500 text-[10px] flex-shrink-0 w-3" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-2 ml-9 rounded-lg bg-zinc-950 border border-zinc-800 p-2">
          {!history ? <p className="text-zinc-500 text-[11px]">Loading…</p> : history.length === 0 ? <p className="text-zinc-500 text-[11px]">No orders found.</p> : (
            <ul className="divide-y divide-zinc-800 max-h-60 overflow-y-auto">
              {history.map(o => (
                <li key={o.id} className="flex items-center gap-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-zinc-200 text-[11px] truncate">{o.show}</p>
                    <p className="text-zinc-500 text-[10px] truncate">
                      {o.at ? new Date(o.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''} · {ORDER_TYPE[o.type] || 'Order'} · {ORDER_STATUS[o.status] || o.status}
                    </p>
                  </div>
                  <span className="text-zinc-200 text-[11px] font-semibold flex-shrink-0">{money(o.amount, o.currency || currency)}</span>
                </li>
              ))}
            </ul>
          )}
          <a href={`https://www.whatnot.com/user/${encodeURIComponent(b.username)}`} target="_blank" rel="noreferrer" className="inline-block mt-2 text-[11px] text-zinc-400 hover:text-white hover:underline">View on Whatnot ↗</a>
        </div>
      )}
    </li>
  );
}

// Good customers who haven't bought for a while: the biggest spenders first
function Lapsed({ buyers, currency, ask, failed }) {
  const cutoff = Date.now() - LAPSED_DAYS * 86400000;
  const lapsed = buyers.filter(b => b.last && b.last < cutoff).slice(0, 10);  // buyers come biggest spenders first
  return (
    <Card title={`Haven't bought in ${LAPSED_DAYS}+ days`}>
      {lapsed.length === 0 ? <p className="text-zinc-500 text-xs">Everyone's bought recently.</p> : (
        <>
          <p className="text-zinc-500 text-[11px] mb-1">Your biggest spenders who've gone quiet. Tap one to see what they used to buy.</p>
          <ul className="divide-y divide-zinc-800">
            {lapsed.map(b => <BuyerRow key={b.username} buyer={b} currency={currency} ask={ask} failed={failed} />)}
          </ul>
        </>
      )}
    </Card>
  );
}

function BuyersTab({ ask, failed, setNote }) {
  const [data, setData] = useState(null);
  const [roleId, setRoleId] = useState('');
  const [minSpend, setMinSpend] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    const answer = await ask('seller.buyers');
    if (!failed(answer)) setData(answer.data);
  }, [ask, failed]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, args) => {
    setBusy(action); setConfirm(''); setNote(null);
    const answer = await ask(action, args);
    setBusy('');
    if (failed(answer)) return;
    setNote({ text: `✅ ${answer.data.message}`, ok: true });
    load();
  };

  const buyers = useMemo(() => asList(data?.buyers), [data]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const rules = asList(data.rules);
  const roles = asList(data.roles);
  const need = Math.round((Number(minSpend) || 0) * 100); // in pence, as the bot has it
  const ready = roleId && need >= 100;
  const qualify = buyers.filter(b => b.member && b.spent >= need).length;
  const words = search.toLowerCase().trim();
  const shown = words ? buyers.filter(b => b.username.includes(words) || (b.member?.name || '').toLowerCase().includes(words)) : buyers;

  return (
    <div className="space-y-4">
      {data.customers === false && (
        <Warning>Your orders are arriving without their buyers, so nobody can be matched. Add the read:customers scope to your Whatnot token, then restart the bot.</Warning>
      )}
      {data.scanning && <Warning>Still reading your order history, so some buyers may not be here yet. Roles are only given for now, never taken back, until it's finished.</Warning>}

      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <Tile label="Buyers" value={count(data.totalBuyers)} sub="have paid for something" />
        <Tile label="In the server" value={count(data.matched)} sub="verified with their username" />
      </div>

      <Card title="Buyer roles">
        <p className="text-zinc-400 text-xs mb-3">
          TOGΞ gives these roles by itself once someone's total spend reaches the amount. It matches each order's buyer to
          the Whatnot username a member verified with. Spend is the price of paid items, before shipping and tax. Giveaway
          wins, unpaid and cancelled orders don't count. It only ever takes back a role that it gave, if cancelled orders
          take them back under the amount.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex-1 min-w-[10rem]">
            <span className="block text-zinc-500 text-[11px] mb-1">Role</span>
            <select value={roleId} onChange={(e) => { setRoleId(e.target.value); setConfirm(''); }} className={`${selectClass} w-full`}>
              <option value="">Choose a role…</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <label className="w-28">
            <span className="block text-zinc-500 text-[11px] mb-1">Spent (£)</span>
            <input type="number" min="1" step="any" inputMode="decimal" placeholder="e.g. 100" value={minSpend} onChange={(e) => { setMinSpend(e.target.value); setConfirm(''); }} className={inputClass} />
          </label>
          <Button tone={confirm === 'add' ? 'success' : 'primary'} disabled={!ready || !!busy}
            onClick={() => (confirm === 'add' ? act('seller.rule.add', { roleId, minSpend: String(minSpend) }) : setConfirm('add'))}>
            {busy === 'seller.rule.add' ? '…' : confirm === 'add' ? `Yes, give it to ${count(qualify)}` : 'Add rule'}
          </Button>
        </div>
        {ready && <p className="text-zinc-400 text-[11px] mt-2">{count(qualify)} member{qualify !== 1 ? 's' : ''} in the server qualif{qualify !== 1 ? 'y' : 'ies'} right now, and more will get it as they buy or verify.</p>}
        <p className="text-zinc-500 text-[11px] mt-2">Only roles TOGΞ can give and without moderator or admin permissions are listed.</p>

        {rules.length > 0 && (
          <ul className="divide-y divide-zinc-800 mt-3 border-t border-zinc-800">
            {rules.map(r => (
              <li key={r.roleId} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-semibold truncate">{r.name}</p>
                  <p className="text-zinc-500 text-[11px]">{r.minSpend != null ? `${money(r.minSpend, data.currency)}+ spent` : `${r.minOrders}+ order${r.minOrders !== 1 ? 's' : ''}`} · {count(r.holders)} ha{r.holders !== 1 ? 've' : 's'} it</p>
                </div>
                <Button small tone={confirm === r.roleId ? 'danger' : 'ghost'} disabled={!!busy}
                  onClick={() => (confirm === r.roleId ? act('seller.rule.remove', { roleId: r.roleId }) : setConfirm(r.roleId))}>
                  {confirm === r.roleId ? 'Sure?' : 'Remove'}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {rules.length > 0 && (
          <div className="mt-3">
            <Button small disabled={!!busy} onClick={() => act('seller.sync')}>{busy === 'seller.sync' ? 'Updating…' : '🔄 Update roles now'}</Button>
          </div>
        )}
      </Card>

      <Lapsed buyers={buyers} currency={data.currency} ask={ask} failed={failed} />

      <Card title={`Top buyers${data.totalBuyers > buyers.length ? ` · top ${buyers.length} of ${count(data.totalBuyers)}` : ''}`}>
        {buyers.length === 0 ? <p className="text-zinc-500 text-xs">No buyers yet.</p> : (
          <>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by username…" aria-label="Search buyers" className={`${inputClass} mb-2`} />
            {shown.length === 0 ? <p className="text-zinc-500 text-xs py-2">Nobody matches that.</p> : (
              <ul className="divide-y divide-zinc-800">
                {shown.map(b => <BuyerRow key={b.username} buyer={b} currency={data.currency} ask={ask} failed={failed} />)}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

const monthLabel = (key, long) => new Date(`${key}-15T12:00:00`).toLocaleDateString(undefined, long ? { month: 'long', year: 'numeric' } : { month: 'short', year: '2-digit' });
const change = (now, before) => (before ? `${now >= before ? '+' : ''}${Math.round(((now - before) / before) * 100)}%` : '—');
const WEEKDAYS = ['Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays', 'Sundays'];
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;

// Monthly totals, each compared with the month before and the same month a year earlier
function MonthlyTotals({ months, currency }) {
  const last = months[months.length - 2];
  const before = months[months.length - 3];
  const yearAgo = months[months.length - 14];
  return (
    <BarChart title="Monthly sales" columns={['Month', 'Sales', 'vs month before', 'vs year before', 'Orders', 'New buyers', 'Returning']} tableNewestFirst
      summary={last ? `${monthLabel(last.month, true)}: ${money(last.sales, currency)}, ${change(last.sales, before?.sales)} on the month before${yearAgo ? ` and ${change(last.sales, yearAgo.sales)} on a year earlier` : ''}.` : ''}
      ariaLabel="Sales for each month over the last two years."
      axis={[monthLabel(months[0].month), 'This month so far']}
      items={months.map((m, i) => {
        const returning = m.buyers - m.newBuyers;
        return {
          key: m.month, value: m.sales,
          detail: `${monthLabel(m.month, true)}: ${money(m.sales, currency)} · ${plural(m.orders, 'order')} · ${count(m.newBuyers)} new, ${count(returning)} returning buyers`,
          cells: [monthLabel(m.month), money(m.sales, currency), change(m.sales, months[i - 1]?.sales), change(m.sales, months[i - 12]?.sales),
            count(m.orders), count(m.newBuyers), count(returning)],
        };
      })} />
  );
}

// When your shows sell best: the average a public show made, by the day and hour it was scheduled to start
function BestTimes({ weekdays, hours, currency }) {
  const avg = (slot) => (slot.shows ? slot.sales / slot.shows : 0);
  const used = hours.map((h, i) => (h.shows ? i : null)).filter(i => i != null);
  const hourSpan = used.length ? hours.slice(used[0], used[used.length - 1] + 1).map((h, i) => ({ ...h, hour: used[0] + i })) : [];
  const best = (slots, name) => {
    const top = slots.filter(s => s.shows).sort((a, b) => avg(b) - avg(a))[0];
    return top ? `Best: ${name(top)}, ${money(avg(top), currency)} a show on average. Hover or tap for each.` : 'No shows yet.';
  };
  const days = weekdays.map((d, i) => ({ ...d, day: i }));
  return (
    <>
      <BarChart title="Best day to stream" columns={['Day', 'Average a show', 'Shows']}
        summary={best(days, d => WEEKDAYS[d.day])} ariaLabel="Average sales per show by the day it started."
        axis={['Mon', 'Sun']}
        items={days.map(d => ({
          key: d.day, value: avg(d), detail: `${WEEKDAYS[d.day]}: ${d.shows ? `${money(avg(d), currency)} a show on average, from ${plural(d.shows, 'show')}` : 'no shows'}`,
          cells: [WEEKDAYS[d.day], d.shows ? money(avg(d), currency) : '—', count(d.shows)],
        }))} />
      {hourSpan.length > 0 && (
        <BarChart title="Best time to start" columns={['Start time', 'Average a show', 'Shows']}
          summary={best(hourSpan, h => hourLabel(h.hour))} ariaLabel="Average sales per show by the hour it started."
          axis={[hourLabel(hourSpan[0].hour), hourLabel(hourSpan[hourSpan.length - 1].hour)]}
          items={hourSpan.map(h => ({
            key: h.hour, value: avg(h), detail: `Starting ${hourLabel(h.hour)}: ${h.shows ? `${money(avg(h), currency)} a show on average, from ${plural(h.shows, 'show')}` : 'no shows'}`,
            cells: [hourLabel(h.hour), h.shows ? money(avg(h), currency) : '—', count(h.shows)],
          }))} />
      )}
    </>
  );
}

const csvCell = (value) => (/[",\n]/.test(String(value)) ? `"${String(value).replace(/"/g, '""')}"` : String(value));

// Every order in a tax year or month, as a spreadsheet file
function Download({ downloads, ask, failed }) {
  const [period, setPeriod] = useState(downloads[0]?.id || '');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    const answer = await ask('seller.export', { period }, 60000);
    setBusy(false);
    if (failed(answer)) return;
    const rows = asList(answer.data.rows).map(asList);
    const lines = [['Date (UK time)', 'Order ID', 'Buyer', 'Type', 'Status', 'Item price', 'Currency', 'Show'].join(',')]
      .concat(rows.map(([at, id, buyer, type, status, amount, currency, show]) => [
        at, id, buyer, ORDER_TYPE[type] || type, ORDER_STATUS[status] || status, ((amount || 0) / 100).toFixed(2), currency, show,
      ].map(csvCell).join(',')));
    const label = downloads.find(d => d.id === period)?.label.split(' (')[0] || period;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `whatnot-orders-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 10000);
  };
  return (
    <Card title="Download orders">
      {downloads.length === 0 ? <p className="text-zinc-500 text-xs">No orders to download yet.</p> : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className={`${selectClass} flex-1 min-w-[12rem]`} aria-label="What to download">
              {downloads.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <Button tone="primary" disabled={!period || busy} onClick={go}>{busy ? 'Preparing…' : '⬇️ Download spreadsheet'}</Button>
          </div>
          <p className="text-zinc-500 text-[11px] mt-2">
            A CSV file that opens in Excel, Numbers or Google Sheets. Every order is in it, including giveaways and cancelled
            ones, so check the Status and Type columns. Item price is before shipping, tax and Whatnot's fees.
          </p>
        </>
      )}
    </Card>
  );
}

function TrendsTab({ ask, failed }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    ask('seller.trends', {}, 60000).then((answer) => { if (!failed(answer)) setData(answer.data); });
  }, [ask, failed]);
  if (!data) return <p className="text-zinc-500 text-xs">Loading…</p>;
  const months = asList(data.months);
  return (
    <div className="space-y-4">
      {months.length > 0 && <MonthlyTotals months={months} currency={data.currency} />}
      {data.showsKnown ? <BestTimes weekdays={asList(data.weekdays)} hours={asList(data.hours)} currency={data.currency} />
        : <Warning>Couldn't read your past shows from Whatnot just now, so best times aren't shown. Try Refresh in a minute.</Warning>}
      <Download downloads={asList(data.downloads)} ask={ask} failed={failed} />
    </div>
  );
}

export default function SellerDashboard({ database, discordUser, isAdmin, getToken, onLogin }) {
  // The same `ask` for the life of the page, as on the TOGΞ Dashboard (the site redraws every second for its clock)
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const ask = useMemo(() => makeAsk(database, () => tokenRef.current()), [database]);
  const [tab, setTab] = useState('overview');
  const [problem, setProblem] = useState(null); // a key of ERRORS
  const [note, setNote] = useState(null);
  const [reload, setReload] = useState(0);

  const failed = useCallback((answer) => {
    if (answer.ok) return false;
    if (ERRORS[answer.error]) setProblem(answer.error);
    else setNote({ text: answer.error, ok: false });
    return true;
  }, []);

  if (!discordUser) return <Gate title="Seller Dashboard" text="Log in with Discord to use the dashboard." onLogin={onLogin} />;
  if (!isAdmin || problem === 'forbidden') return <Gate title="Seller Dashboard" text={ERRORS.forbidden} />;
  if (problem === 'login' || !getToken()) return <Gate title="Seller Dashboard" text={ERRORS.login} onLogin={onLogin} />;

  const shared = { ask, failed, setNote };
  return (
    <main className="flex-1">
      <div className="max-w-3xl mx-auto px-4 pt-2 pb-10">
        <div className="flex items-center gap-3 mb-4">
          <img src="/wnlogo.png" alt="" className="h-10 w-10 rounded-xl object-contain" />
          <div className="min-w-0 flex-1">
            <h1 className="text-white text-lg font-bold leading-tight">Seller Dashboard</h1>
            <p className="text-zinc-400 text-[11px] truncate">{problem ? ERRORS[problem] : 'Private: only you can see this'}</p>
          </div>
          <Button tone="ghost" small onClick={() => { setNote(null); setProblem(null); setReload(r => r + 1); }}>↻ Refresh</Button>
        </div>

        <div className="mb-4 -mx-4 px-4 overflow-x-auto">
          <div className="flex gap-1 p-1 rounded-lg bg-zinc-900 border border-zinc-800 w-max">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => { setTab(id); setNote(null); }}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors whitespace-nowrap ${tab === id ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <Notice note={note} />

        <TabBoundary key={`${tab}-${reload}`}>
          {tab === 'overview' && <OverviewTab {...shared} />}
          {tab === 'shows' && <ShowsTab {...shared} />}
          {tab === 'buyers' && <BuyersTab {...shared} />}
          {tab === 'trends' && <TrendsTab {...shared} />}
        </TabBoundary>
      </div>
    </main>
  );
}
